import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { fmtIDR, fmtShortIDR, firstOfMonthISO, todayISO } from "@/lib/format";
import { Building2, TrendingUp, Users, Receipt } from "lucide-react";

export default function ConsolidatedDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());

  const load = () => {
    setLoading(true);
    api.get("/dashboard/consolidated", { params: { date_from: dateFrom, date_to: dateTo } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const rows = data?.branches || [];
  const totals = data?.totals || { revenue: 0, tx_count: 0, expense: 0, net_profit: 0, patients: 0 };
  const chartData = useMemo(() => rows.map((r) => ({ name: r.branch_name, Revenue: r.revenue, "Laba Bersih": r.net_profit })), [rows]);

  const applyPreset = (kind) => {
    const now = new Date();
    if (kind === "this_month") { setDateFrom(firstOfMonthISO()); setDateTo(todayISO()); }
    else if (kind === "last_month") {
      const f = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const l = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(f.toISOString().slice(0, 10)); setDateTo(l.toISOString().slice(0, 10));
    } else if (kind === "30d") {
      const f = new Date(now); f.setDate(f.getDate() - 29);
      setDateFrom(f.toISOString().slice(0, 10)); setDateTo(todayISO());
    }
  };

  return (
    <div className="space-y-6" data-testid="consolidated-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Ringkasan Konsolidasi</h1>
          <p className="mt-1 text-sm text-muted-foreground">Perbandingan kinerja seluruh cabang dalam satu tampilan.</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div><Label className="text-xs">Dari</Label><Input type="date" data-testid="consol-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
          <div><Label className="text-xs">Sampai</Label><Input type="date" data-testid="consol-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
          <Button size="sm" variant="outline" onClick={() => applyPreset("this_month")}>Bulan Ini</Button>
          <Button size="sm" variant="outline" onClick={() => applyPreset("last_month")}>Bulan Lalu</Button>
          <Button size="sm" variant="outline" onClick={() => applyPreset("30d")}>30 Hari</Button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp className="h-3.5 w-3.5" /> Total Revenue</div><div className="font-heading text-2xl" data-testid="consol-total-revenue">{fmtIDR(totals.revenue)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-xs text-muted-foreground"><Receipt className="h-3.5 w-3.5" /> Total Transaksi</div><div className="font-heading text-2xl">{totals.tx_count}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Laba Bersih Gabungan</div><div className={`font-heading text-2xl ${totals.net_profit < 0 ? "text-rose-600" : "text-emerald-600"}`} data-testid="consol-total-net">{fmtIDR(totals.net_profit)}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Total Pasien</div><div className="font-heading text-2xl">{totals.patients}</div></CardContent></Card>
      </div>

      {/* Chart */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Revenue vs Laba Bersih per Cabang</CardTitle></CardHeader>
        <CardContent style={{ height: 300 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => fmtShortIDR(v)} tick={{ fontSize: 11 }} width={70} />
                <Tooltip formatter={(v) => fmtIDR(v)} />
                <Bar dataKey="Revenue" fill="#0f172a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Laba Bersih" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="grid place-items-center h-full text-sm text-muted-foreground">Belum ada data cabang.</div>}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Building2 className="h-5 w-5" /> Rincian per Cabang</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cabang</TableHead>
                <TableHead className="text-right">Revenue</TableHead>
                <TableHead className="text-right">Transaksi</TableHead>
                <TableHead className="text-right">AOV</TableHead>
                <TableHead className="text-right">Beban</TableHead>
                <TableHead className="text-right">Laba Bersih</TableHead>
                <TableHead className="text-right">Pasien</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Memuat...</TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Belum ada cabang.</TableCell></TableRow>}
              {!loading && rows.map((r) => (
                <TableRow key={r.branch_id} data-testid={`consol-row-${r.branch_id}`}>
                  <TableCell className="font-medium">{r.branch_name}</TableCell>
                  <TableCell className="text-right">{fmtIDR(r.revenue)}</TableCell>
                  <TableCell className="text-right">{r.tx_count}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmtIDR(r.aov)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmtIDR(r.expense)}</TableCell>
                  <TableCell className={`text-right font-medium ${r.net_profit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtIDR(r.net_profit)}</TableCell>
                  <TableCell className="text-right">{r.patients}</TableCell>
                </TableRow>
              ))}
              {!loading && rows.length > 0 && (
                <TableRow className="font-semibold bg-secondary/40">
                  <TableCell>TOTAL</TableCell>
                  <TableCell className="text-right">{fmtIDR(totals.revenue)}</TableCell>
                  <TableCell className="text-right">{totals.tx_count}</TableCell>
                  <TableCell className="text-right">—</TableCell>
                  <TableCell className="text-right">{fmtIDR(totals.expense)}</TableCell>
                  <TableCell className={`text-right ${totals.net_profit < 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmtIDR(totals.net_profit)}</TableCell>
                  <TableCell className="text-right">{totals.patients}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
